import React from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  Typography
} from '@mui/material';

import PartyHealthChip from '../../../../../components/utils/PartyHealthChip';
import { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import Copyable from '../../../../../components/utils/Copyable';
import NA from '../../../../../components/utils/NA';

import style from "../../../../../global.scss";
import "../../../../../components/modals/styles.scss";

const Field = ({ label, value, copyable }) => (
  <Box minWidth={"150px"} flex={"1 1 150px"} mb={"10px"}>
    <Typography variant='caption' color={"grey"} display={"block"}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ wordBreak: "break-all" }}>
      {
        (value === null || value === undefined || value === "")
        ? <NA />
        : copyable ? <Copyable>{String(value)}</Copyable> : String(value)
      }
    </Typography>
  </Box>
);

const Section = ({ title, accent, count, children }) => (
  <Box mb={"18px"}>
    <Box className='flexCenter_Row' gap={"10px"} mb={"8px"}>
      <Typography variant='subtitle2' sx={{ color: accent }}>
        {title}
      </Typography>
      <Chip size='small' label={count} sx={{ background: `${accent}20`, color: accent }} />
    </Box>
    {children}
  </Box>
);

/**
 * Everything on file for one customer. Note the asymmetry with vendors: the
 * receive side has no domestic configuration table, so a domestic customer has
 * nothing to show here and nothing repair can check.
 */
const CustomerDetailsModal = ({ open, setOpen, customer }) => {
  const handleClose = () => setOpen(false);

  if (!customer) return null;

  const international = customer.associated_international_customer || [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="customer-details-title">
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box className="modal" maxWidth={"900px"} width={"90vw"}>
          <Box sx={{ margin: "15px 15px 10px 15px" }}>
            <Box className='flexCenterSBRow'>
              <Typography variant='h6' sx={{ color: style["primary"] }}>
                {customer.name || "Customer"}
              </Typography>
              <PartyHealthChip health={customer.health} />
            </Box>
            <Typography variant='caption' color={"grey"}>
              {customer.health?.detail}
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ margin: "15px", maxHeight: "65vh", overflowY: "auto" }}>

            <Section title="Customer" accent={TILE_ACCENT.neutral} count={1}>
              <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                <Field label="Customer id" value={customer.customer_id} copyable />
                <Field label="Contact" value={customer.contact_name} />
                <Field label="Email" value={customer.email} />
                <Field label="Phone" value={[customer.phone_country_code, customer.phone].filter(Boolean).join(" ")} />
                <Field label="Payment term" value={customer.default_payment_term} />
                <Field label="Active" value={customer.is_active ? "Yes" : "No"} />
                <Field label="Domestic" value={customer.is_domestic ? "Yes" : "No"} />
                <Field label="International" value={customer.is_international ? "Yes" : "No"} />
                <Field label="External" value={customer.is_external ? "Yes" : "No"} />
                <Field label="Party id" value={customer.ref_party_id} copyable />
                <Field label="Company" value={customer.ref_company_id?.company_name} />
                <Field label="Company id" value={customer.ref_company_id?.company_id} copyable />
              </Box>
            </Section>

            <Section title="Billing" accent={TILE_ACCENT.neutral} count={1}>
              <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                <Field label="Currency" value={customer.billing_currency} />
                <Field label="Address" value={[customer.billing_address, customer.billing_address2, customer.billing_city, customer.billing_state, customer.billing_country, customer.billing_postal_code].filter(Boolean).join(", ")} />
              </Box>
            </Section>

            <Section title="Shipping" accent={TILE_ACCENT.neutral} count={1}>
              <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                <Field label="Ship to" value={customer.ship_to} />
                <Field label="Address" value={[customer.shipping_address, customer.shipping_address2, customer.shipping_city, customer.shipping_state, customer.shipping_country, customer.shipping_postal_code].filter(Boolean).join(", ")} />
              </Box>
            </Section>

            <Section
              title="International configuration · rcv_customer_international"
              accent={international.length ? TILE_ACCENT.good : TILE_ACCENT.warn}
              count={international.length}>
              {
                international.length === 0
                ?
                <Typography variant='body2' color={"grey"}>
                  No international configuration on file.
                </Typography>
                :
                international.map((row, index) => (
                  <Box
                    key={index}
                    mb={"10px"}
                    sx={{
                      border: "1px solid rgba(44, 45, 47, 1)",
                      borderRadius: "10px",
                      padding: "12px",
                    }}>
                    <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                      <Field label="Row id" value={row.id} copyable />
                      <Field label="Country" value={row.country} />
                      <Field label="Currency" value={row.currency} />
                      <Field label="Created" value={row.created_at} />
                      <Field label="Updated" value={row.updated_at} />
                    </Box>
                  </Box>
                ))
              }
            </Section>

            <Section title="Domestic configuration" accent={TILE_ACCENT.info} count={0}>
              <Typography variant='body2' color={"grey"}>
                The receive side has no domestic configuration table — there is no
                customer equivalent of pay_vendor_account. A domestic customer is
                therefore reported as unverifiable and repair never clears the flag
                on its own.
              </Typography>
            </Section>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='outlined' onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default CustomerDetailsModal;
